// Turning checked spreadsheet rows into created records.
//
// ./importSheet answers "does this sheet make sense on its own". This module
// answers the two questions that need the database: does every name in it point
// at something real, and what happened when we tried to create it.
//
// ── Old ids where they exist, names everywhere else ───────────────────────────
// A shop's old software has its own customer 3, scheme 1, floor 1, account 513.
// Those numbers mean nothing in this app's tables — every imported record gets a
// fresh auto-increment id — so most of the sheet is written in NAMES ("GOLD 12M",
// "GF-A-12", a mobile number) and resolved against this store's masters before a
// single row is sent.
//
// The two exceptions are the two that matter, because they are what the old
// TRANSACTIONS actually reference: the CUSTOMER and the ACCOUNT. The old table
// records a loan against a customer number and an account key, not a name. So
// customer_master and account_master remember what they used to be called in
// `old_id` (migration 030), and where the sheet supplies that key it WINS over
// every name-based match — there is nothing left to infer.
//
// Which matters most for customers: this old app's customer table has no
// party-code column at all, so without the old id the only identifier left is
// a mobile number, which several people legitimately share.
//
// Anything that does not resolve, or resolves to more than one person, is
// reported as a problem row instead of being guessed at: a loan attached to the
// wrong customer is worse than a loan not imported.
//
// ── One row at a time, on purpose ─────────────────────────────────────────────
// Rows go up in small chunks and the server creates each one in its own
// transaction, so a bad row cannot roll back its neighbours and a re-run skips
// what already exists. That makes an interrupted import safe to simply run
// again — which matters when a shop is bringing across five thousand loans over
// a hotel wifi connection.
import axios from "axios";

import { API_BASE_URL } from "../pawnCustomer/shared";

// How many records ride in one request. Small enough that a dropped connection
// costs little, big enough that 5,000 loans is 200 requests and not 5,000.
export const CHUNK_SIZE = 25;

const norm = (v) =>
  String(v ?? "")
    .trim()
    .toUpperCase();

const digits = (v) => String(v ?? "").replace(/\D/g, "");

// An old app's record key, normalised. Kept identical to normalizeOldId in
// pawnImportController.js — if you change one, change both.
//
// The trailing-".0" strip is the load-bearing part: an id column in Excel is a
// NUMBER cell, and xlsx hands 412 back as "412" from one file and "412.0" from
// the next. Unnormalised, the same account imports twice.
const oldKey = (v) => {
  const raw = String(v ?? "").trim();
  if (!raw) return null;
  return raw.replace(/\.0+$/, "").toUpperCase();
};

const numOrNull = (v) =>
  v === null || v === undefined || v === "" ? null : Number(v);

// ─── Masters ──────────────────────────────────────────────────────────────────
// Everything a sheet can name, fetched once per import and turned into lookup
// maps. Customers come as a LIST per key rather than a single value: one mobile
// number can belong to several people, and the resolver has to be able to say
// "which of these two?" rather than silently take the first.
const pushTo = (map, key, value) => {
  if (!key) return;
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
};

const fetchAllCustomers = async (storeId) => {
  const PAGE = 500;
  let page = 1;
  let total = Infinity;
  const all = [];
  while (all.length < total) {
    const { data } = await axios.get(
      `${API_BASE_URL}/api/pawn/customers/store/${storeId}`,
      { params: { page, limit: PAGE } }
    );
    if (!data?.success)
      throw new Error(data?.statusMessage || "Customer list failed");
    const rows = data.data || [];
    all.push(...rows);
    total = data.pagination?.total ?? all.length;
    if (!rows.length) break;
    page += 1;
  }
  return all;
};

export const loadMasters = async (storeId) => {
  const get = async (url, params) => {
    try {
      const { data } = await axios.get(url, params ? { params } : undefined);
      return data?.data || [];
    } catch {
      return [];
    }
  };

  const [customers, schemes, floors, metals, purities, accounts] =
    await Promise.all([
      fetchAllCustomers(storeId),
      get(`${API_BASE_URL}/api/pawn/schemes/${storeId}`),
      get(`${API_BASE_URL}/api/jewel-master/floors/store/${storeId}`),
      get(
        `${API_BASE_URL}/api/jewel-master/metal-type-master/store/${storeId}`
      ),
      get(`${API_BASE_URL}/api/jewel-master/purity-master/store/${storeId}`),
      get(`${API_BASE_URL}/api/accounts/accounts/${storeId}/accounts-only`),
    ]);

  const byMobile = new Map();
  const byParty = new Map();
  const byAadhar = new Map();
  for (const c of customers) {
    pushTo(byMobile, digits(c.mobile), c);
    pushTo(byParty, norm(c.party_code), c);
    pushTo(byAadhar, digits(c.aadhar), c);
  }

  const schemeBy = new Map();
  for (const s of schemes) schemeBy.set(norm(s.scheme_name), s);

  const floorBy = new Map();
  for (const f of floors) {
    floorBy.set(norm(f.name), f);
    if (f.floor_quick_no) floorBy.set(norm(f.floor_quick_no), f);
  }

  const metalBy = new Map();
  for (const m of metals) {
    metalBy.set(norm(m.name), m);
    if (m.code) metalBy.set(norm(m.code), m);
  }
  // Where an unrecognised metal name lands, so one odd spelling in a decade-old
  // export cannot cost the shop an ornament. Gold unless this store has none.
  const defaultMetal =
    metals.find((m) => norm(m.name) === "GOLD") || metals[0] || null;

  // Purities are looked up inside a metal: "916" means one thing under GOLD and
  // nothing at all under SILVER.
  const purityByMetal = new Map();
  for (const p of purities) {
    const list = purityByMetal.get(p.metal_type_id) || [];
    list.push(p);
    purityByMetal.set(p.metal_type_id, list);
  }

  const accountBy = new Map();
  const accountByOldId = new Map();
  for (const a of accounts) {
    accountBy.set(norm(a.account_name || a.name), a);
    if (a.account_code) accountBy.set(norm(a.account_code), a);
    // The old chart's key. Unlike the name, this cannot collide: account_master
    // has no unique index on account_name, so two accounts may share one and
    // the name map above silently keeps only the last.
    const key = oldKey(a.old_id);
    if (key && !accountByOldId.has(key)) accountByOldId.set(key, a);
  }

  // Customers by their old-app id. Fetched from its own endpoint rather than
  // read off the customer list, for two reasons: the list pages 500 at a time
  // and is filtered to cust_role 'primary', and this way one request covers a
  // whole migration however many customers the store has.
  const customerByOldId = new Map();
  try {
    const { data } = await axios.get(
      `${API_BASE_URL}/api/pawn/customers/store/${storeId}/old-ids`
    );
    for (const row of data?.data || []) {
      const key = oldKey(row.old_id);
      if (key && !customerByOldId.has(key))
        customerByOldId.set(key, { customer_id: row.id, name: row.name });
    }
  } catch {
    // Leaves the map empty, so rows fall back to mobile / Aadhar / party code.
  }

  return {
    counts: {
      customers: customers.length,
      schemes: schemes.length,
      floors: floors.length,
      metals: metals.length,
      purities: purities.length,
      accounts: accounts.length,
    },
    customers: { byMobile, byParty, byAadhar, byOldId: customerByOldId },
    schemeBy,
    floorBy,
    metalBy,
    defaultMetal,
    purityByMetal,
    accountBy,
    accountByOldId,
  };
};

// ─── Resolvers ────────────────────────────────────────────────────────────────
// Each returns { value, problem }. A problem is a sentence for the operator, in
// their words, naming what to do about it.

// Party code first (a code is a code), then Aadhar (identifies one person), then
// mobile (a contact number, which several people may share).
const resolveCustomer = (v, masters) => {
  const { byParty, byAadhar, byMobile, byOldId } = masters.customers;

  // The OLD CUSTOMER ID wins outright, before anything else is even looked at.
  //
  // It is the only identifier that is exactly what the old software itself
  // recorded against this loan — the old table stores a customer NUMBER, not a
  // name or a phone number — so when the sheet supplies it there is nothing to
  // infer and no ambiguity to resolve. Every other column is a guess by
  // comparison: a mobile can belong to several people, and this shop's old
  // customer table has no party-code column at all.
  //
  // An old id that is filled in but matches nothing STOPS the row rather than
  // falling through to the mobile. The operator has told us precisely which
  // customer this is; quietly attaching the loan to whoever shares a phone
  // number would be worse than importing nothing.
  const oldIdRaw = oldKey(v.customerOldId);
  if (oldIdRaw) {
    const hit = byOldId?.get(oldIdRaw);
    if (hit) return { value: hit.customer_id };
    return {
      problem: `No customer in the app carries Old Customer ID "${oldIdRaw}" — import that customer in the Customers sheet first (fill its Old Customer ID column), or clear this cell to match on mobile instead`,
    };
  }

  const tries = [
    { list: byParty.get(norm(v.customerPartyCode)), label: "party code" },
    { list: byAadhar.get(digits(v.customerAadhar)), label: "Aadhar" },
    { list: byMobile.get(digits(v.customerMobile)), label: "mobile number" },
  ].filter((t) => (t.list || []).length);

  if (!tries.length)
    return {
      problem:
        "No customer in the app matches this row — import the Customers sheet first, or fill the Old Customer ID column (surest), or check the mobile / Aadhar / party code",
    };

  // When several columns are filled they must agree; the narrowest wins.
  const hit = tries[0];
  if (hit.list.length === 1) return { value: hit.list[0].customer_id };

  // More than one person behind that key. If another column narrows it, use the
  // intersection; otherwise say so rather than picking one.
  const ids = new Set(hit.list.map((c) => c.customer_id));
  for (const t of tries.slice(1)) {
    const narrowed = t.list.filter((c) => ids.has(c.customer_id));
    if (narrowed.length === 1) return { value: narrowed[0].customer_id };
  }
  return {
    problem: `${hit.list.length} customers share this ${hit.label} — fill the Old Customer ID column, or add the Aadhar or party code, so the loan goes to the right person`,
  };
};

// The bank / UPI account a voucher's non-cash leg moved through.
//
// Old key first, name second — for the same reason as the customer above, plus
// one specific to accounts: account_master has NO unique index on account_name,
// so a store may legitimately hold two accounts called "CASH" and the name map
// keeps only one of them. The old key cannot collide that way.
//
// Unlike the customer, an unresolvable account never blocks the row. It is one
// leg of a voucher that still balances without it (the payout falls back to
// cash), and losing a decade-old loan over a bank account that was closed in
// 2019 would help nobody.
const resolveBankAccount = (v, masters) => {
  const key = oldKey(v.bankAccountOldId);
  if (key) {
    const hit = masters.accountByOldId?.get(key);
    if (hit) return { value: hit };
    return {
      value: null,
      warning: `No account carries Old Account Key "${key}" — imported without the bank leg, which was booked as cash. Import the Accounts sheet first, or name the account instead.`,
    };
  }

  if (!v.bankAccount) return { value: null };
  return resolveNamed(v.bankAccount, masters.accountBy, {
    label: "Bank Account",
    required: false,
    hint: "the accounts master",
  });
};

// A name that does not match anything in the store's masters is a WARNING, not
// a rejection. The job here is to get a shop's history into the app; a scheme
// they renamed in 2019, or a floor that no longer exists, is not a reason to
// leave a real loan behind. Only the things a row cannot physically be written
// without — its customer, its loan — are allowed to block, and those say so at
// their own call sites.
//
// `required` therefore only governs a BLANK cell. Everything unmatched comes
// back as { value: null, warning } and the caller decides what to put there.
const resolveNamed = (raw, map, { label, required, hint }) => {
  const key = norm(raw);
  if (!key)
    return required
      ? { value: null, warning: `${label} is empty` }
      : { value: null };
  const hit = map.get(key);
  if (!hit)
    return {
      value: null,
      warning: `${label} "${String(
        raw
      ).trim()}" is not in ${hint} — imported without it. Add it there and set it on the record afterwards.`,
    };
  return { value: hit };
};

// The ornament's purity: matched inside its metal, by name/code or by the
// percentage itself, falling back to the metal's default. ornaments_details
// needs BOTH an id and a percentage, and neither may be null.
const resolvePurity = (raw, metal, masters) => {
  const list = masters.purityByMetal.get(metal?.id) || [];
  if (!list.length)
    return {
      problem: `${
        metal?.name || "This metal"
      } has no purities set up — add them under Metal & Purity first`,
    };

  const key = norm(raw);
  const asNumber = Number(String(raw ?? "").replace(/[^\d.]/g, ""));

  const byName = list.find((p) => norm(p.name) === key || norm(p.code) === key);
  if (byName)
    return { value: { id: byName.id, percent: Number(byName.purity_percent) } };

  if (isFinite(asNumber) && asNumber > 0) {
    const byPercent = list.find(
      (p) => Math.abs(Number(p.purity_percent) - asNumber) < 0.01
    );
    if (byPercent)
      return {
        value: { id: byPercent.id, percent: Number(byPercent.purity_percent) },
      };
    // A percentage nobody has a record for is still a real percentage — keep
    // the shop's number and hang it off the metal's default purity, rather than
    // refusing a whole loan over a purity row that was never created.
    const dflt = list.find((p) => p.is_default) || list[0];
    return {
      value: { id: dflt.id, percent: asNumber },
      warning: `Purity ${asNumber}% is not in the master — kept as typed, filed under ${dflt.name}`,
    };
  }

  const dflt = list.find((p) => p.is_default) || list[0];
  return {
    value: { id: dflt.id, percent: Number(dflt.purity_percent) },
    warning: `No purity given — used ${metal?.name}'s default, ${dflt.name}`,
  };
};

// ─── Grouped sheets: many rows, one record ───────────────────────────────────
// A loan with four ornaments is four rows. The first row of a group carries the
// loan; every row carries an ornament.
export const groupRecords = (rows, spec) => {
  if (!spec.groupBy) return rows.map((r) => ({ head: r, all: [r] }));
  const out = [];
  let current = null;
  for (const row of rows) {
    if (!row.isContinuation || !current) {
      current = { head: row, all: [row] };
      out.push(current);
    } else {
      current.all.push(row);
    }
  }
  return out;
};

// ─── Sheet row → API payload ─────────────────────────────────────────────────
const resolveLoan = (record, masters, { pawnMode } = {}) => {
  const v = record.head.values;
  const problems = [];
  const warnings = [];

  // The ONLY thing that can stop a loan: nobody to attach it to. cust_id is
  // NOT NULL, and a loan booked against the wrong person is worse than a loan
  // that has to be imported a second time.
  const customer = resolveCustomer(v, masters);
  if (customer.problem) problems.push(customer.problem);

  // A scheme the shop renamed years ago must not cost them the loan. scheme_id
  // is nullable, and without one the interest engine simply uses the loan's own
  // rate — which this sheet carries.
  const scheme = resolveNamed(v.scheme, masters.schemeBy, {
    label: "Scheme",
    required: false,
    hint: "Scheme Master",
  });
  if (scheme.warning)
    warnings.push(`${scheme.warning} The loan's own Interest % is used.`);

  const floor = resolveNamed(v.floor, masters.floorBy, {
    label: "Floor",
    required: false,
    hint: "the Floor master",
  });
  if (floor.warning) warnings.push(floor.warning);

  let bankAccountId = null;
  {
    const acc = resolveBankAccount(v, masters);
    if (acc.warning) warnings.push(acc.warning);
    if (acc.value) bankAccountId = acc.value.account_id ?? acc.value.id ?? null;
  }

  const ornaments = [];
  for (const row of record.all) {
    const o = row.values;
    if (!o.itemName && !o.itemGross && !o.itemNet) continue;

    // ornaments_details needs a metal id and it cannot be null, so an
    // unrecognised metal falls back to the store's default rather than throwing
    // the ornament — and with it the weights the floor report runs on — away.
    const named = resolveNamed(o.metalType, masters.metalBy, {
      label: "Metal Type",
      required: false,
      hint: "the Metal & Purity master",
    });
    const metalValue = named.value || masters.defaultMetal;
    if (!metalValue) {
      problems.push(
        `Row ${row.rowNo}: this store has no metal types set up — add them under Metal & Purity before importing ornaments`
      );
      continue;
    }
    if (named.warning)
      warnings.push(
        `Row ${row.rowNo}: ${named.warning} Filed under ${metalValue.name}.`
      );
    const metal = { value: metalValue };

    const purity = resolvePurity(o.purityPercentage, metal.value, masters);
    if (purity.problem) {
      problems.push(`Row ${row.rowNo}: ${purity.problem}`);
      continue;
    }
    if (purity.warning) warnings.push(`Row ${row.rowNo}: ${purity.warning}`);

    ornaments.push({
      item_name: o.itemName,
      item_pcs: o.itemPcs ?? 1,
      item_gross: o.itemGross ?? 0,
      item_net: o.itemNet ?? o.itemGross ?? 0,
      metal_type_id: metal.value.id,
      metal_type_rate: o.metalRate ?? 0,
      purity_id: purity.value.id,
      purity_percentage: purity.value.percent,
      item_value: o.itemValue ?? 0,
    });
  }

  return {
    problems,
    warnings,
    payload: {
      row_no: record.head.rowNo,
      voucher_no: v.voucherNo,
      ledger_voucher_no: v.ledgerVoucherNo || null,
      transaction_date: v.loanDate,
      collect_by_date: v.collectByDate || null,
      payment_time: v.loanTime || null,
      cust_id: customer.value ?? null,
      scheme_id: scheme.value?.id ?? null,
      floor_id: floor.value?.id ?? null,
      nominee_name: v.nomineeName || null,
      loan_amount: v.loanAmount,
      // The scheme's rate for THIS book: a scheme carries an official rate and
      // an unofficial one (interest_2), and an unofficial loan priced at the
      // official rate would be wrong from its very first receipt.
      loan_interest:
        v.interestRate ??
        (Number(pawnMode) === 2
          ? scheme.value?.interest_2 ?? scheme.value?.interest
          : scheme.value?.interest) ??
        0,
      insurance_percentage: v.insuranceRate ?? 0,
      one_month_deduct: v.oneMonthDeduct ? 1 : 0,
      other_charges: v.otherCharges ?? 0,
      // The old app's own first-month figure when it kept one. Blank ⇒ the
      // importer derives loan × rate, which is what most exports need.
      interest_amount: numOrNull(v.firstMonthInterest),
      int_upto_date: v.interestUptoDate || null,
      principal_repaid: numOrNull(v.principalRepaid),
      // A loan that was already closed in the old software comes across closed,
      // in one step, off its own settled columns.
      is_settled: v.settled ? "Y" : "N",
      settled_date: v.settledDate || null,
      tare_wt: numOrNull(v.tareWeight),
      transaction_ref: v.transactionRef || null,
      remarks: v.remarks || null,
      cash_amount: numOrNull(v.cashAmount),
      bank_amount: numOrNull(v.bankAmount),
      bank_account_id: bankAccountId,
      ornaments,
    },
  };
};

const resolveReceipt = (record, masters, { settlement }) => {
  const v = record.head.values;
  const warnings = [];

  let bankAccountId = null;
  {
    const acc = resolveBankAccount(v, masters);
    if (acc.warning) warnings.push(acc.warning);
    if (acc.value) bankAccountId = acc.value.account_id ?? acc.value.id ?? null;
  }

  return {
    problems: [],
    warnings,
    payload: {
      row_no: record.head.rowNo,
      loan_voucher_no: v.loanVoucherNo,
      // The old app's own number for this receipt IS its ledger voucher — the
      // number the shop's books and their customer's card already carry.
      ledger_voucher_no: v.receiptNo || v.settlementReceiptNo || null,
      receipt_date: v.receiptDate || v.settlementDate,
      settlement: settlement ? 1 : 0,
      amount: v.amount ?? 0,
      interest_due: numOrNull(v.interestDue),
      interest_deducted: v.interestDeducted ?? 0,
      insurance_deducted: v.insuranceDeducted ?? 0,
      interest_discount: v.interestDiscount ?? 0,
      other_charges: v.otherCharges ?? 0,
      principal_reduction: numOrNull(v.principalReduction),
      paid_up_to_date: v.paidUpToDate || null,
      transaction_ref: v.transactionRef || null,
      remarks:
        [v.releasedTo ? `Released to ${v.releasedTo}` : null, v.remarks]
          .filter(Boolean)
          .join(" — ") || null,
      cash_amount: numOrNull(v.cashAmount),
      bank_amount: numOrNull(v.bankAmount),
      bank_account_id: bankAccountId,
    },
  };
};

// ─── Accounts ─────────────────────────────────────────────────────────────────
// The one dataset that needs no masters. The sheet is self-contained — a name
// and the key the account had in the old app — so there is nothing to look up
// and therefore nothing that can fail to resolve. No account row is ever
// blocked here; whether one is a duplicate is the server's call, since only it
// can see what is already in the store.
const resolveAccount = (record) => {
  const v = record.head.values;
  return {
    problems: [],
    warnings: [],
    payload: {
      row_no: record.head.rowNo,
      account_name: v.accountName,
      old_id: oldKey(v.oldAccountKey),
    },
  };
};

// Which resolver, which endpoint, which payload key — per dataset.
const DATASETS = {
  accounts: {
    resolve: resolveAccount,
    url: "/api/pawn/import/accounts",
    key: "accounts",
  },
  loans: { resolve: resolveLoan, url: "/api/pawn/import/loans", key: "loans" },
  receipts: {
    resolve: (r, m, o) => resolveReceipt(r, m, { ...o, settlement: false }),
    url: "/api/pawn/import/receipts",
    key: "receipts",
  },
  settlements: {
    resolve: (r, m, o) => resolveReceipt(r, m, { ...o, settlement: true }),
    url: "/api/pawn/import/receipts",
    key: "receipts",
  },
};

/**
 * Resolves every checked row against the store's masters.
 * Returns { ready, blocked } — `ready` rows carry a payload, `blocked` ones
 * carry the sentence that says why not.
 */
export const resolveAll = (rows, spec, masters, opts = {}) => {
  const dataset = DATASETS[spec.key];
  if (!dataset) return { ready: [], blocked: [] };

  const ready = [];
  const blocked = [];
  for (const record of groupRecords(rows, spec)) {
    const { problems, warnings, payload } = dataset.resolve(
      record,
      masters,
      opts
    );
    if (problems.length)
      blocked.push({
        rowNo: record.head.rowNo,
        group: record.head.group,
        problems,
      });
    else ready.push({ payload, warnings, rowNo: record.head.rowNo });
  }
  return { ready, blocked };
};

/** Dates a dataset touches, for the pre-import readiness check. */
export const datesOf = (ready) =>
  Array.from(
    new Set(
      ready
        .map((r) => r.payload.transaction_date || r.payload.receipt_date)
        .filter(Boolean)
    )
  );

/** Is the store ready at all — posting accounts set, financial years present. */
export const checkReadiness = async ({ storeId, dates }) => {
  const { data } = await axios.post(
    `${API_BASE_URL}/api/pawn/import/readiness`,
    {
      store_id: storeId,
      dates,
    }
  );
  if (!data?.success)
    throw new Error(data?.statusMessage || "Readiness check failed");
  return data.data;
};

/**
 * Sends the resolved rows up in chunks.
 *
 * `onProgress({ done, total, results })` fires after every chunk so the dialog
 * can move a bar and show what has landed. Set `dryRun` to run the server's own
 * checks — duplicates, financial years, the money adding up — WITHOUT creating
 * anything.
 */
export const runImport = async ({
  spec,
  ready,
  storeId,
  userId,
  pawnMode,
  // Which old database this batch came from, for shops migrating more than one
  // (two counter PCs = two old DBs = one store). Empty is the normal case and
  // still deduplicates correctly — see migration 030.
  oldSource = "",
  dryRun = false,
  onProgress,
  shouldStop,
}) => {
  const dataset = DATASETS[spec.key];
  if (!dataset) throw new Error(`No importer for ${spec.key}`);

  const warningsByRow = new Map(
    ready.map((r) => [r.payload.row_no, r.warnings || []])
  );
  const results = [];
  const total = ready.length;

  for (let i = 0; i < total; i += CHUNK_SIZE) {
    if (shouldStop?.()) break;
    const slice = ready.slice(i, i + CHUNK_SIZE).map((r) => r.payload);

    let chunkResults;
    try {
      const { data } = await axios.post(`${API_BASE_URL}${dataset.url}`, {
        store_id: storeId,
        user_id: userId,
        pawn_mode: pawnMode,
        old_source: oldSource,
        dry_run: dryRun,
        [dataset.key]: slice,
      });
      if (!data?.success)
        throw new Error(data?.statusMessage || "Import failed");
      chunkResults = data.data.results || [];
    } catch (error) {
      // A dead connection must not look like a rejected row: every record in
      // this chunk is reported as failed WITH the reason, and a re-run will
      // pick them up again because nothing was created.
      const message =
        error?.response?.data?.statusMessage ||
        error?.message ||
        "Could not reach the server";
      chunkResults = slice.map((p) => ({
        row_no: p.row_no,
        voucher_no: p.voucher_no || p.loan_voucher_no || p.account_name,
        outcome: "failed",
        message,
        warnings: [],
      }));
    }

    // Carry the resolution-time warnings through to the row's final verdict, so
    // the report has everything about a row in one place.
    for (const r of chunkResults) {
      const extra = warningsByRow.get(r.row_no) || [];
      results.push(
        extra.length ? { ...r, warnings: [...extra, ...(r.warnings || [])] } : r
      );
    }
    onProgress?.({ done: Math.min(i + CHUNK_SIZE, total), total, results });
  }

  return {
    results,
    created: results.filter((r) => r.outcome === "created").length,
    ready: results.filter((r) => r.outcome === "ready").length,
    skipped: results.filter((r) => r.outcome === "skipped").length,
    failed: results.filter((r) => r.outcome === "failed").length,
    warned: results.filter((r) => (r.warnings || []).length).length,
  };
};

/**
 * Moves the store's voucher counters past everything just imported.
 *
 * Imported vouchers carry their own numbers and never touch the counters, so
 * without this the next ticket written at the counter collides with a loan from
 * 2019. Safe to call more than once — it only ever moves a counter forward.
 */
export const resyncSequences = async ({ storeId, pawnMode }) => {
  const { data } = await axios.post(
    `${API_BASE_URL}/api/pawn/import/resync-sequences`,
    { store_id: storeId, pawn_mode: pawnMode }
  );
  return data?.data?.moved || [];
};
